import React from 'react';
import { Box, Typography } from '@mui/material';

import { useEligibilityBadgeState } from '../hooks/useEligibilityBadgeState';
import { ELIG_TOKENS } from '../theme/readinessTokens';
import { formatSourceProvenance } from '../utils/provenance';

import { DetailSection, Field } from './EligibilityFinancialsBlock';

interface Props {
  patientId?: string;
  encounterId?: string;
}

/**
 * Coverage block — plan + member-ID picture for this visit. Production
 * EligibilityCheckResponse exposes a leaner subset than the Nirvana
 * snapshot the prototype renders (no plan_name, plan_type, group_id at
 * this level), so we lead with what's reliable: coverage status, plan
 * status, member ID with mismatch correction surfaced.
 */
export const EligibilityCoverageBlock: React.FC<Props> = ({ patientId, encounterId }) => {
  const { eligibilityData: data } = useEligibilityBadgeState({ patientId, encounterId });
  if (!data) return null;
  const provenance = formatSourceProvenance(data.source);

  const coverageStatus = humanize(data.coverage_status);
  const planStatus = humanize(data.plan_status);
  const payerName = data.payer_name?.trim();
  const memberId = data.corrected_member_id ?? data.submitted_member_id;
  const memberIdCorrected =
    data.corrected_member_id != null &&
    data.submitted_member_id != null &&
    data.corrected_member_id !== data.submitted_member_id;

  const hasAnything = payerName || coverageStatus || planStatus || memberId || data.service_tier;
  if (!hasAnything) return null;

  return (
    <DetailSection title="Coverage" provenance={provenance}>
      {payerName && <Field label="Payer" value={payerName} />}
      {coverageStatus && <Field label="Coverage" value={coverageStatus} />}
      {planStatus && <Field label="Plan status" value={planStatus} />}
      {data.service_tier && <Field label="Network" value={humanize(data.service_tier)} />}
      {memberId && (
        <Box>
          <Field label="Member ID" value={memberId} />
          {memberIdCorrected && (
            <Typography
              variant="caption"
              sx={{
                color: ELIG_TOKENS.amber,
                display: 'block',
                mt: -0.25,
                pl: '50%',
              }}
            >
              Corrected from {data.submitted_member_id}
            </Typography>
          )}
        </Box>
      )}
    </DetailSection>
  );
};

/**
 * Best-effort enum → label conversion. Snake_case becomes Title Case;
 * already-readable strings pass through. Strict guard: anything outside the
 * snake_case shape is rendered verbatim (trust the BE if it sent prose).
 */
function humanize(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!/^[a-z][a-z0-9_]*$/.test(trimmed)) return trimmed;
  return trimmed
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
